import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { useGetTelehealthSessionInfo } from '../hooks';
import { useVideoPlatform } from '../hooks/useVideoPlatform';
import { telehealthAdmitStatusIndicatorGate } from '../statsig/gates';
import { VideoPlatform, normalizeVideoPlatform } from '../video-sdk';
import { ClientQueueToast, ClientQueueToastProps } from './ClientQueueToast';
import { useAdmittedClientStatus } from './admission/useAdmittedClientStatus';
import { useVerifyClientCompatibilityCallback } from './admission/useVerifyClientCompatibilityCallback';
import {
  inSessionPatientInformationAtom,
  joiningVisitorIdFamily,
  providerShortIdAtom,
  waitingVisitorsAtom,
} from './state';

type WrapperProps = Omit<
  ClientQueueToastProps,
  | 'admittedClientStatus'
  | 'appointment'
  | 'isAppointmentLoading'
  | 'buttonIsLoading'
  | 'clientNameAnnotation'
  | 'currentSessionPatientShortId'
  | 'granularStatusEnabled'
  | 'providerCanSupportAllClients'
>;

export function ClientQueueToastWrapper(props: WrapperProps) {
  const { participantName, patientShortId, visitorUuid, closeToast } = props;
  const isWaitingForClientToJoin = useAtomValue(joiningVisitorIdFamily(visitorUuid ?? ''));
  const visitorPresenceMap = useAtomValue(waitingVisitorsAtom);
  const providerShortId = useAtomValue(providerShortIdAtom);
  const { patientShortId: currentSessionPatientShortId = null } = useAtomValue(inSessionPatientInformationAtom) ?? {};

  const { value: granularStatusEnabled } = useFeatureGate(telehealthAdmitStatusIndicatorGate);
  const admittedClientStatus = useAdmittedClientStatus({ visitorUuid: visitorUuid ?? '' });

  const { data, loading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointment = data?.telehealthSessionInfo?.appointment;
  const verifyClientCompatibility = useVerifyClientCompatibilityCallback();
  const isIncompatibleClient = !!visitorUuid && !verifyClientCompatibility(visitorUuid);
  const isZoom = normalizeVideoPlatform(useVideoPlatform()) === VideoPlatform.Zoom;

  useEffect(
    function dismissToastWhenVisitorLeaves() {
      // Dismiss the toast if the visitor has left the waiting room OR in the case of a multi-visitor generic toast, if all visitors have left
      if (
        (visitorUuid && !visitorPresenceMap[visitorUuid]) ||
        (!visitorUuid && !Object.keys(visitorPresenceMap).length)
      ) {
        closeToast();
      }
    },
    [closeToast, visitorPresenceMap, visitorUuid],
  );

  return (
    <ClientQueueToast
      {...props}
      admittedClientStatus={admittedClientStatus}
      buttonIsLoading={isWaitingForClientToJoin}
      currentSessionPatientShortId={currentSessionPatientShortId}
      granularStatusEnabled={granularStatusEnabled}
      isIncompatible={isIncompatibleClient}
      isZoom={isZoom}
      participantName={participantName}
      appointment={appointment}
      isAppointmentLoading={loading}
    />
  );
}
